import { useState, useEffect } from 'react';
import { X, ShoppingCart, Loader2 } from 'lucide-react';
import { DbMeal, DbIngredient, DbAddon } from '@/types/database';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { formatPrice } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface MealIngredientWithDetails {
  id: string;
  ingredient_id: string;
  is_removable: boolean;
  ingredient: DbIngredient;
}

interface MealAddonWithDetails {
  id: string;
  addon_id: string;
  addon: DbAddon;
}

interface MealCustomizationModalDbProps {
  meal: DbMeal;
  onClose: () => void;
}

export function MealCustomizationModalDb({ meal, onClose }: MealCustomizationModalDbProps) {
  const [notes, setNotes] = useState('');
  const [removedIngredients, setRemovedIngredients] = useState<string[]>([]);
  const [selectedAddons, setSelectedAddons] = useState<string[]>([]);
  const [mealIngredients, setMealIngredients] = useState<MealIngredientWithDetails[]>([]);
  const [mealAddons, setMealAddons] = useState<MealAddonWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();
  const { toast } = useToast();

  useEffect(() => {
    const fetchMealDetails = async () => {
      setLoading(true);
      try {
        // Fetch ingredients for this meal
        const { data: ingredientsData, error: ingredientsError } = await supabase
          .from('meal_ingredients')
          .select(`
            id,
            ingredient_id,
            is_removable,
            ingredient:ingredients(*)
          `)
          .eq('meal_id', meal.id);

        if (ingredientsError) throw ingredientsError;

        // Fetch addons for this meal
        const { data: addonsData, error: addonsError } = await supabase
          .from('meal_addons')
          .select(`
            id,
            addon_id,
            addon:addons(*)
          `)
          .eq('meal_id', meal.id);

        if (addonsError) throw addonsError;

        setMealIngredients(
          (ingredientsData || []).map((item: any) => ({
            id: item.id,
            ingredient_id: item.ingredient_id,
            is_removable: item.is_removable,
            ingredient: item.ingredient,
          }))
        );

        setMealAddons(
          (addonsData || []).map((item: any) => ({
            id: item.id,
            addon_id: item.addon_id,
            addon: item.addon,
          }))
        );
      } catch (error) {
        console.error('Error fetching meal details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMealDetails();
  }, [meal.id]);

  const toggleIngredientRemoval = (ingredientId: string) => {
    setRemovedIngredients((prev) =>
      prev.includes(ingredientId)
        ? prev.filter((id) => id !== ingredientId)
        : [...prev, ingredientId]
    );
  };

  const toggleAddon = (addonId: string) => {
    setSelectedAddons((prev) =>
      prev.includes(addonId)
        ? prev.filter((id) => id !== addonId)
        : [...prev, addonId]
    );
  };

  const calculateTotalPrice = () => {
    let total = meal.price_in_cents;
    selectedAddons.forEach((addonId) => {
      const addon = mealAddons.find((a) => a.addon_id === addonId);
      if (addon) {
        total += addon.addon.price_in_cents;
      }
    });
    return total;
  };

  // Convert DbMeal to the format expected by CartContext
  const cartMeal = {
    id: meal.id,
    name: meal.name_he,
    description: meal.description_he || meal.description || '',
    priceInCents: meal.price_in_cents,
    image: meal.image_url || '/placeholder.svg',
    calories: meal.calories || 0,
    protein: meal.protein || 0,
    carbs: meal.carbs || 0,
    fat: meal.fat || 0,
    category: meal.category as any,
    ingredients: mealIngredients.map((mi) => ({
      id: mi.ingredient_id,
      name: mi.ingredient.name_he,
      removable: mi.is_removable,
      isAllergen: mi.ingredient.is_allergen,
      allergenType: mi.ingredient.allergen_type || undefined,
    })),
    addons: mealAddons.map((ma) => ({
      id: ma.addon_id,
      name: ma.addon.name_he,
      priceInCents: ma.addon.price_in_cents,
    })),
  };

  const handleAddToCart = () => {
    // Get the names of removed ingredients and selected addons for the order
    const removedIngredientNames = mealIngredients
      .filter((mi) => removedIngredients.includes(mi.ingredient_id))
      .map((mi) => mi.ingredient.name_he);

    const selectedAddonNames = mealAddons
      .filter((ma) => selectedAddons.includes(ma.addon_id))
      .map((ma) => ma.addon.name_he);

    addItem(cartMeal, removedIngredientNames, selectedAddonNames, notes || undefined);
    toast({
      title: 'נוסף לסל',
      description: `${meal.name_he} נוסף לסל הקניות`,
    });
    onClose();
  };

  const removableIngredients = mealIngredients.filter((mi) => mi.is_removable);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-card rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden animate-scale-in flex flex-col">
        {/* Header Image */}
        <div className="relative h-40 sm:h-56 bg-secondary flex-shrink-0">
          <img
            src={meal.image_url || '/placeholder.svg'}
            alt={meal.name_he}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.src = '/placeholder.svg';
            }}
          />
          <button
            onClick={onClose}
            className="absolute top-3 start-3 p-2 bg-card/90 rounded-full hover:bg-card transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-2">
            {meal.name_he}
          </h2>
          <p className="text-muted-foreground mb-4 text-sm sm:text-base">
            {meal.description_he || meal.description}
          </p>

          {/* Nutrition Info */}
          {(meal.calories || meal.protein || meal.carbs || meal.fat) && (
            <div className="grid grid-cols-4 gap-1 sm:gap-2 mb-4 sm:mb-6">
              {meal.calories && (
                <div className="text-center p-1.5 sm:p-2 bg-secondary rounded-lg">
                  <p className="text-[10px] sm:text-xs text-muted-foreground">קלוריות</p>
                  <p className="font-bold text-foreground text-sm sm:text-base">{meal.calories}</p>
                </div>
              )}
              {meal.protein && (
                <div className="text-center p-1.5 sm:p-2 bg-secondary rounded-lg">
                  <p className="text-[10px] sm:text-xs text-muted-foreground">חלבון</p>
                  <p className="font-bold text-foreground text-sm sm:text-base">{meal.protein}g</p>
                </div>
              )}
              {meal.carbs && (
                <div className="text-center p-1.5 sm:p-2 bg-secondary rounded-lg">
                  <p className="text-[10px] sm:text-xs text-muted-foreground">פחמימות</p>
                  <p className="font-bold text-foreground text-sm sm:text-base">{meal.carbs}g</p>
                </div>
              )}
              {meal.fat && (
                <div className="text-center p-1.5 sm:p-2 bg-secondary rounded-lg">
                  <p className="text-[10px] sm:text-xs text-muted-foreground">שומן</p>
                  <p className="font-bold text-foreground text-sm sm:text-base">{meal.fat}g</p>
                </div>
              )}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : (
            <>
              {/* Removable Ingredients */}
              {removableIngredients.length > 0 && (
                <div className="mb-4 sm:mb-6">
                  <h3 className="text-base sm:text-lg font-semibold text-foreground mb-2 sm:mb-3">
                    הסרת רכיבים
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground mb-2 sm:mb-3">
                    סמנו את הרכיבים שברצונכם להסיר מהמנה
                  </p>
                  <div className="space-y-1.5 sm:space-y-2">
                    {removableIngredients.map((mi) => (
                      <label
                        key={mi.ingredient_id}
                        className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg border border-border hover:bg-secondary/50 cursor-pointer transition-colors"
                      >
                        <Checkbox
                          checked={removedIngredients.includes(mi.ingredient_id)}
                          onCheckedChange={() => toggleIngredientRemoval(mi.ingredient_id)}
                        />
                        <span className="flex-1 text-foreground text-sm sm:text-base">
                          {mi.ingredient.name_he}
                        </span>
                        {mi.ingredient.is_allergen && (
                          <span className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 bg-destructive/10 text-destructive rounded-full">
                            אלרגן
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Addons */}
              {mealAddons.length > 0 && (
                <div className="mb-4 sm:mb-6">
                  <h3 className="text-base sm:text-lg font-semibold text-foreground mb-2 sm:mb-3">
                    תוספות
                  </h3>
                  <div className="space-y-1.5 sm:space-y-2">
                    {mealAddons.map((ma) => (
                      <label
                        key={ma.addon_id}
                        className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg border border-border hover:bg-secondary/50 cursor-pointer transition-colors"
                      >
                        <Checkbox
                          checked={selectedAddons.includes(ma.addon_id)}
                          onCheckedChange={() => toggleAddon(ma.addon_id)}
                        />
                        <span className="flex-1 text-foreground text-sm sm:text-base">
                          {ma.addon.name_he}
                        </span>
                        <span className="text-primary font-medium text-sm sm:text-base">
                          +{formatPrice(ma.addon.price_in_cents)}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Notes */}
          <div className="mb-2">
            <label className="block text-sm font-medium text-foreground mb-2">
              הערות מיוחדות
            </label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="הוסיפו הערות או בקשות מיוחדות..."
              className="resize-none"
              rows={2}
            />
          </div>
        </div>

        {/* Footer - Fixed at bottom */}
        <div className="p-3 sm:p-4 border-t border-border bg-card flex-shrink-0">
          <Button
            onClick={handleAddToCart}
            className="w-full gap-2 text-base sm:text-lg py-5 sm:py-6"
            size="lg"
            disabled={loading}
          >
            <ShoppingCart className="h-5 w-5" />
            הוספה לסל - {formatPrice(calculateTotalPrice())}
          </Button>
        </div>
      </div>
    </div>
  );
}
